"use client";

import { DatePicker } from "@/components/date-picker";
import { TimePicker } from "@/components/time-picker";
import {
  isDateDisabled,
  isDayOpen,
  isTimeInSlot,
} from "@/lib/availabilityUtils";
import { cn, getTranslatedName as utilsGetTranslatedName } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { environment } from "@/config/env";
import { useCart } from "@/contexts/CartContext";
import { useGuestAuth } from "@/contexts/GuestAuthContext";
import {
  PrivacyPolicyDialog,
  useStructurePrivacy,
} from "@/components/PrivacyPolicyDialog";
import type { CheckedState } from "@radix-ui/react-checkbox";
import axios from "axios";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Info,
  Minus,
  Plus,
  RotateCcw,
  Settings2,
  ShoppingCart,
  Trash2,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "./ui/field";

export interface CartProps {
  className?: string;
  size?: "default" | "sm" | "lg" | "icon" | "icon-sm" | "icon-lg";
}

export function Cart({ className, size = "icon" }: CartProps = {}) {
  const { t, i18n } = useTranslation("cart");
  const commonTranslations = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [expandedCustomSchedule, setExpandedCustomSchedule] = useState<
    Record<string, boolean>
  >({});
  const [startDateTimeErrors, setStartDateTimeErrors] = useState<
    Record<
      string,
      {
        startDate: boolean;
        startTime: boolean;
        endDate: boolean;
        endTime: boolean;
      }
    >
  >({});
  const { privacyPolicyHtml } = useStructurePrivacy();
  const [isPrivacyInfoDialogOpen, setIsPrivacyInfoDialogOpen] = useState(false);

  const [checkoutForm, setCheckoutForm] = useState({
    name: "",
    surname: "",
    email: "",
    notes: "",
    mandatoryConditions: false,
    marketing: false,
    roomNumber: "",
  });
  const [formErrors, setFormErrors] = useState({
    name: false,
    surname: false,
    email: false,
    mandatoryConditions: false,
  });
  const {
    items,
    removeItem,
    updateQuantity,
    updateItemDates,
    updateItemTimes,
    resetItemScheduleToGlobal,
    globalStartDate,
    globalEndDate,
    globalStartTime,
    globalEndTime,
    setGlobalSchedule,
    totalItems,
    totalPrice,
    clearCart,
  } = useCart();
  const { profile, refreshProfile } = useGuestAuth();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (profile) {
      setCheckoutForm((f) => ({
        ...f,
        name: f.name || profile.name || "",
        surname: f.surname || profile.surname || "",
        email: f.email || profile.email || "",
      }));
    }
  }, [profile]);

  useEffect(() => {
    if (!globalStartDate && items.length > 0) {
      const firstArrival = items.find((i) => i.reservation?.arrival)
        ?.reservation?.arrival;
      if (firstArrival) {
        setGlobalSchedule({
          startDate: new Date(firstArrival),
          endDate: globalEndDate,
          startTime: globalStartTime ?? "10:00",
          endTime: globalEndTime ?? "11:00",
        });
      } else {
        setGlobalSchedule({
          startDate: new Date(),
          endDate: globalEndDate,
          startTime: globalStartTime ?? "10:00",
          endTime: globalEndTime ?? "11:00",
        });
      }
    }
  }, [
    items,
    globalStartDate,
    globalEndDate,
    globalStartTime,
    globalEndTime,
    setGlobalSchedule,
  ]);

  // Helpers
  const toDateOnlyString = (date?: Date) => {
    if (!date) return undefined;
    return date.toISOString().split("T")[0];
  };

  const toTimeOnlyString = (time?: string) => {
    if (!time) return undefined;
    const normalizedTime = time.trim();
    return /^\d{2}:\d{2}$/.test(normalizedTime)
      ? `${normalizedTime}:00`
      : normalizedTime;
  };

  const onOpenInfoPrivacyPolicy = () => {
    setIsPrivacyInfoDialogOpen(true);
  };

  const confirmCart = async () => {
    const errors = {
      name: !checkoutForm.name.trim(),
      surname: !checkoutForm.surname.trim(),
      email: !checkoutForm.email.trim(),
      mandatoryConditions: !checkoutForm.mandatoryConditions,
    };
    setFormErrors(errors);

    if (Object.values(errors).some(Boolean)) return;

    setIsLoading(true);
    const structureId = localStorage.getItem("structureId");

    items.map((item) => {
      const reservationDeparture = item.reservation?.departure
        ? toDateOnlyString(new Date(item.reservation.departure))
        : undefined;
      const itemEndDate = item.endDate
        ? toDateOnlyString(new Date(item.endDate))
        : undefined;

      if (
        reservationDeparture &&
        itemEndDate &&
        reservationDeparture < itemEndDate
      ) {
        toast.error(t("confirm_error"));
        setIsLoading(false);
        return;
      }
    });

    const requestBody = {
      ...checkoutForm,
      reservationId: items[0]?.reservation?.id || null,
      siteLanguage: i18n.language,
      services: items.map((item) => ({
        serviceId: item.serviceId,
        subServiceId: item.subServiceId,
        quantity: item.quantity,
        startDate: toDateOnlyString(item.startDate),
        endDate: toDateOnlyString(item.endDate),
        startTime: toTimeOnlyString(item.startTime),
        endTime: toTimeOnlyString(item.endTime),
        price: item.price,
      })),
    };

    try {
      axios.defaults.baseURL = environment.ELITE_BASE_URI;
      await axios.post(
        `concierge/${structureId}/request-services`,
        requestBody,
      );
      toast.success(t("confirm_success"));
      setCheckoutForm({
        name: "",
        surname: "",
        email: "",
        notes: "",
        mandatoryConditions: false,
        marketing: false,
        roomNumber: "",
      });
      clearCart();
      setIsOpen(false);
      setShowCheckout(false);
      await refreshProfile();
    } catch (err) {
      toast.error(t("confirm_error"));
    } finally {
      setIsLoading(false);
    }
  };

  function onCheckOut() {
    let isStartDateTimeValid = true;
    const nextStartDateTimeErrors: Record<
      string,
      {
        startDate: boolean;
        startTime: boolean;
        endDate: boolean;
        endTime: boolean;
      }
    > = {};
    const validationErrors: string[] = [];

    const weekdayKeys = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];

    const dayLabelsIt: Record<string, string> = {
      sunday: "Domenica",
      monday: "Lunedì",
      tuesday: "Martedì",
      wednesday: "Mercoledì",
      thursday: "Giovedì",
      friday: "Venerdì",
      saturday: "Sabato",
    };

    const timeToMinutes = (t: string) => {
      const [h, m] = t.split(":").map(Number);
      return (h ?? 0) * 60 + (m ?? 0);
    };

    items.forEach((item) => {
      const itemName = getTranslatedName(item.name);
      const hasStartDateError = !item.startDate;
      const hasStartTimeError = !item.startTime;

      if (item.reservation) {
        setCheckoutForm((f) => ({
          ...f,
          name: item.reservation.name,
          surname: item.reservation.surname,
          email: item.reservation.email,
          roomNumber:
            (
              item.reservation.rooms[0]?.description +
              " " +
              item.reservation.rooms[0]?.roomNumber
            ).trim() || "",
        }));
      }

      if (hasStartDateError || hasStartTimeError) {
        isStartDateTimeValid = false;
        nextStartDateTimeErrors[String(item.id)] = {
          startDate: hasStartDateError,
          startTime: hasStartTimeError,
          endDate: false,
          endTime: false,
        };
        validationErrors.push(
          `Seleziona data e ora di inizio per: "${itemName}"`,
        );
      }

      if (item.endDate && !item.endTime) {
        isStartDateTimeValid = false;
        nextStartDateTimeErrors[String(item.id)] = {
          ...(nextStartDateTimeErrors[String(item.id)] ?? {
            startDate: false,
            startTime: false,
            endDate: false,
            endTime: false,
          }),
          endTime: true,
        };
        validationErrors.push(`Seleziona l'ora di fine per: "${itemName}"`);
      }

      if (
        item.startDate &&
        item.endDate &&
        toDateOnlyString(new Date(item.startDate)) ===
          toDateOnlyString(new Date(item.endDate)) &&
        item.startTime &&
        item.endTime &&
        item.startTime > item.endTime
      ) {
        isStartDateTimeValid = false;
        nextStartDateTimeErrors[String(item.id)] = {
          ...(nextStartDateTimeErrors[String(item.id)] ?? {
            startDate: false,
            startTime: false,
            endDate: false,
            endTime: false,
          }),
          endTime: true,
        };
        validationErrors.push(
          `L'ora di inizio deve essere precedente all'ora di fine per: "${itemName}"`,
        );
      }

      if (item.data && item.serviceAvailability === 1) {
        let slot: Record<string, boolean | string | undefined> | null = null;
        try {
          const parsed = JSON.parse(item.data);
          slot = Array.isArray(parsed) ? parsed[0] : parsed;
        } catch {
          slot = null;
        }

        if (slot) {
          const isTimeInSlots = (
            dayKey: string,
            time: string,
            slotData: Record<string, boolean | string | undefined>,
          ) => {
            const t = timeToMinutes(time);
            const s1 = slotData[`${dayKey}StartTime`] as string | undefined;
            const e1 = slotData[`${dayKey}EndTime`] as string | undefined;
            const s2 = slotData[`${dayKey}SecondStartTime`] as
              | string
              | undefined;
            const e2 = slotData[`${dayKey}SecondEndTime`] as string | undefined;
            const inSlot1 =
              s1 && e1
                ? t >= timeToMinutes(s1) && t <= timeToMinutes(e1)
                : false;
            const inSlot2 =
              s2 && e2
                ? t >= timeToMinutes(s2) && t <= timeToMinutes(e2)
                : false;
            return inSlot1 || inSlot2;
          };

          if (item.startDate) {
            const d = new Date(item.startDate);
            const startDayKey = weekdayKeys[d.getDay()] ?? "sunday";
            const openVal = slot[startDayKey];
            const dayOpen =
              openVal === true || openVal === "true" || openVal === 1;
            const timeValid =
              !item.startTime ||
              isTimeInSlots(startDayKey, item.startTime, slot);

            if (!dayOpen) {
              isStartDateTimeValid = false;
              nextStartDateTimeErrors[String(item.id)] = {
                ...(nextStartDateTimeErrors[String(item.id)] ?? {
                  startDate: false,
                  startTime: false,
                  endDate: false,
                  endTime: false,
                }),
                startDate: true,
              };
              validationErrors.push(
                `Il servizio "${itemName}" non è disponibile di ${dayLabelsIt[startDayKey] ?? startDayKey}`,
              );
            } else if (!timeValid) {
              isStartDateTimeValid = false;
              nextStartDateTimeErrors[String(item.id)] = {
                ...(nextStartDateTimeErrors[String(item.id)] ?? {
                  startDate: false,
                  startTime: false,
                  endDate: false,
                  endTime: false,
                }),
                startTime: true,
              };
              validationErrors.push(
                `L'orario ${item.startTime} per "${itemName}" non rientra nelle fasce di apertura`,
              );
            }
          }
        }
      }
    });

    setStartDateTimeErrors(nextStartDateTimeErrors);

    if (!isStartDateTimeValid) {
      const uniqueErrors = Array.from(new Set(validationErrors));
      if (uniqueErrors.length > 0) {
        uniqueErrors.slice(0, 3).forEach((msg) => toast.error(msg));
      } else {
        toast.error("Controlla date e orari selezionati prima di proseguire.");
      }
      return;
    }

    setShowCheckout(true);
  }

  function getTranslatedName(value: any): string {
    return utilsGetTranslatedName(value, i18n.language);
  }

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size={size} className={cn("relative", className)}>
          <ShoppingCart className="h-4 w-4" />
          {totalItems > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
              {totalItems}
            </span>
          )}
        </Button>
      </SheetTrigger>

      <SheetContent className="flex w-full flex-col sm:max-w-lg px-4">
        <SheetHeader className="space-y-2.5">
          <SheetTitle className="flex items-center gap-2">
            {showCheckout && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setShowCheckout(false)}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            {showCheckout ? t("checkout") : `${t("cart")} (${totalItems})`}
          </SheetTitle>
        </SheetHeader>

        <Separator />

        <div className="flex-1 min-h-0 overflow-hidden">
          {!showCheckout ? (
            <ScrollArea className="h-full">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center space-y-2">
                  <ShoppingCart className="h-12 w-12 text-muted-foreground/50" />
                  <p className="text-muted-foreground">{t("emptyLabel")}</p>
                </div>
              ) : (
                <div className="space-y-4 pr-1">
                  {/* Blocco Data e Ora Generali per il Carrello */}
                  <div className="rounded-xl border bg-muted/40 p-3 shadow-xs">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="h-4 w-4 text-primary" />
                      <div>
                        <h3 className="text-xs font-semibold leading-none text-foreground">
                          Data e Ora Prenotazione Generali
                        </h3>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Applicate a tutti i servizi nel carrello
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-2 pt-1">
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase font-medium text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {t("inizio")}
                        </Label>
                        <div className="flex gap-1.5">
                          <DatePicker
                            value={globalStartDate}
                            className="h-8 flex-1 text-xs"
                            minDate={
                              items[0]?.reservation?.arrival
                                ? new Date(items[0].reservation.arrival)
                                : new Date()
                            }
                            isDateDisabled={(date) =>
                              items.length > 0 &&
                              items.every((item) =>
                                isDateDisabled(
                                  date,
                                  item.serviceAvailability ?? 0,
                                  item.data ?? undefined,
                                  item.maxDailyQuantity,
                                  item.bookedDailyQuantities,
                                ),
                              )
                            }
                            onChange={(date) => {
                              setGlobalSchedule({
                                startDate: date,
                                endDate: globalEndDate,
                                startTime: globalStartTime,
                                endTime: globalEndTime,
                              });
                            }}
                            placeholder={commonTranslations.t(
                              "documentVerification.placeholders.datePlaceholder",
                            )}
                          />
                          <TimePicker
                            value={globalStartTime ?? "10:00"}
                            filterTime={(time) =>
                              items.length === 0 ||
                              items.some((item) =>
                                isTimeInSlot(
                                  time,
                                  globalStartDate || new Date(),
                                  item.serviceAvailability ?? 0,
                                  item.data ?? undefined,
                                ),
                              )
                            }
                            onChange={(time) => {
                              setGlobalSchedule({
                                startDate: globalStartDate,
                                endDate: globalEndDate,
                                startTime: time,
                                endTime: globalEndTime,
                              });
                            }}
                            className="h-8 w-28 text-xs"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase font-medium text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {t("fine")}
                        </Label>
                        <div className="flex gap-1.5">
                          <DatePicker
                            value={globalEndDate}
                            minDate={globalStartDate || new Date()}
                            isDateDisabled={(date) =>
                              items.length > 0 &&
                              items.every((item) =>
                                isDateDisabled(
                                  date,
                                  item.serviceAvailability ?? 0,
                                  item.data ?? undefined,
                                  item.maxDailyQuantity,
                                  item.bookedDailyQuantities,
                                ),
                              )
                            }
                            onChange={(date) => {
                              setGlobalSchedule({
                                startDate: globalStartDate,
                                endDate: date,
                                startTime: globalStartTime,
                                endTime: globalEndTime,
                              });
                            }}
                            placeholder={commonTranslations.t(
                              "documentVerification.placeholders.datePlaceholder",
                            )}
                            className="h-8 flex-1 text-xs"
                          />
                          <TimePicker
                            value={globalEndTime ?? "11:00"}
                            filterTime={(time) =>
                              items.length === 0 ||
                              items.some((item) =>
                                isTimeInSlot(
                                  time,
                                  globalEndDate ||
                                    globalStartDate ||
                                    new Date(),
                                  item.serviceAvailability ?? 0,
                                  item.data ?? undefined,
                                ),
                              )
                            }
                            onChange={(time) => {
                              setGlobalSchedule({
                                startDate: globalStartDate,
                                endDate: globalEndDate,
                                startTime: globalStartTime,
                                endTime: time,
                              });
                            }}
                            className="h-8 w-28 text-xs"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {items.map((item) => (
                    <Card key={item.id}>
                      <CardContent className="p-3 flex flex-col gap-3">
                        {(() => {
                          const itemStartErrors = startDateTimeErrors[
                            String(item.id)
                          ] ?? {
                            startDate: false,
                            startTime: false,
                            endDate: false,
                            endTime: false,
                          };

                          return (
                            <>
                              <div className="flex gap-3">
                                <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-md border bg-muted">
                                  {item.image ? (
                                    <img
                                      src={item.image}
                                      alt={item.name}
                                      className="h-full w-full object-cover"
                                    />
                                  ) : (
                                    <ShoppingCart className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 text-muted-foreground" />
                                  )}
                                </div>

                                <div className="flex flex-1 flex-col justify-between">
                                  <div className="flex items-start justify-between">
                                    <h4 className="text-sm font-semibold leading-none">
                                      {getTranslatedName(item.name)}
                                    </h4>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                      onClick={() => removeItem(item.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>

                                  <p className="text-sm text-muted-foreground">
                                    €{item.price?.toFixed(2)}
                                  </p>

                                  {item.isQuantityFixed == false && (
                                    <div className="flex items-center gap-2 mt-2">
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-7 w-7"
                                        onClick={() =>
                                          updateQuantity(
                                            item.id,
                                            item.quantity - 1,
                                          )
                                        }
                                      >
                                        <Minus className="h-3 w-3" />
                                      </Button>
                                      <span className="w-6 text-center text-sm">
                                        {item.quantity}
                                      </span>
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-7 w-7"
                                        onClick={() =>
                                          updateQuantity(
                                            item.id,
                                            item.quantity + 1,
                                          )
                                        }
                                      >
                                        <Plus className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Indicatore & Controllo Orario per Singolo Servizio */}
                              <div className="mt-1 rounded-lg border bg-muted/20 p-2 text-xs">
                                <div className="flex items-center justify-between gap-1.5 flex-wrap">
                                  <div className="flex items-center gap-1.5 min-w-0">
                                    <Calendar className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                    <span className="truncate text-muted-foreground text-[11px]">
                                      {item.startDate
                                        ? new Date(
                                            item.startDate,
                                          ).toLocaleDateString()
                                        : "Data non impostata"}
                                      {item.startTime
                                        ? ` • ${item.startTime}`
                                        : ""}
                                      {item.endTime ? ` - ${item.endTime}` : ""}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-1 shrink-0 ml-auto">
                                    {item.isCustomSchedule ? (
                                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 text-[10px] font-medium border border-amber-500/20">
                                        Personalizzato
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 text-[10px] font-medium border border-emerald-500/20">
                                        Generale
                                      </span>
                                    )}

                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-1.5 text-[11px] text-muted-foreground hover:text-foreground"
                                      onClick={() => {
                                        setExpandedCustomSchedule((prev) => ({
                                          ...prev,
                                          [String(item.id)]:
                                            !prev[String(item.id)],
                                        }));
                                      }}
                                    >
                                      <Settings2 className="h-3 w-3 mr-0.5" />
                                      {expandedCustomSchedule[String(item.id)]
                                        ? "Chiudi"
                                        : "Personalizza"}
                                    </Button>

                                    {item.isCustomSchedule && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 px-1.5 text-[11px] text-amber-600 hover:text-amber-700 dark:text-amber-400"
                                        title="Ripristina orario generale"
                                        onClick={() =>
                                          resetItemScheduleToGlobal(item.id)
                                        }
                                      >
                                        <RotateCcw className="h-3 w-3" />
                                      </Button>
                                    )}
                                  </div>
                                </div>

                                {/* Form di override personalizzato espandibile */}
                                {expandedCustomSchedule[String(item.id)] && (
                                  <div className="mt-2.5 grid grid-cols-1 gap-2 pt-2 border-t border-border/40">
                                    <div className="space-y-1">
                                      <Label className="text-[10px] uppercase text-muted-foreground">
                                        {t("inizio")}
                                      </Label>
                                      <div className="flex gap-2">
                                        <DatePicker
                                          value={item.startDate}
                                          className={`h-8 flex-1 text-xs ${itemStartErrors.startDate ? "border-destructive" : ""}`}
                                          minDate={
                                            item.reservation?.arrival ||
                                            new Date()
                                          }
                                          isDateDisabled={(date) =>
                                            isDateDisabled(
                                              date,
                                              item.serviceAvailability ?? 0,
                                              item.data ?? undefined,
                                              item.maxDailyQuantity,
                                              item.bookedDailyQuantities,
                                            )
                                          }
                                          onChange={(date) => {
                                            updateItemDates(
                                              item.id,
                                              date,
                                              item.endDate,
                                            );
                                            setStartDateTimeErrors((prev) => ({
                                              ...prev,
                                              [String(item.id)]: {
                                                startDate: false,
                                                startTime:
                                                  prev[String(item.id)]
                                                    ?.startTime ?? false,
                                                endDate:
                                                  prev[String(item.id)]
                                                    ?.endDate ?? false,
                                                endTime:
                                                  prev[String(item.id)]
                                                    ?.endTime ?? false,
                                              },
                                            }));
                                          }}
                                          placeholder={commonTranslations.t(
                                            "documentVerification.placeholders.datePlaceholder",
                                          )}
                                        />
                                        <TimePicker
                                          value={item.startTime ?? ""}
                                          filterTime={(time) =>
                                            isTimeInSlot(
                                              time,
                                              item.startDate || new Date(),
                                              item.serviceAvailability ?? 0,
                                              item.data ?? undefined,
                                            )
                                          }
                                          onChange={(time) => {
                                            updateItemTimes(
                                              item.id,
                                              time,
                                              item.endTime,
                                            );
                                            setStartDateTimeErrors((prev) => ({
                                              ...prev,
                                              [String(item.id)]: {
                                                startDate:
                                                  prev[String(item.id)]
                                                    ?.startDate ?? false,
                                                startTime: false,
                                                endDate:
                                                  prev[String(item.id)]
                                                    ?.endDate ?? false,
                                                endTime:
                                                  prev[String(item.id)]
                                                    ?.endTime ?? false,
                                              },
                                            }));
                                          }}
                                          className={`h-8 w-28 text-xs ${itemStartErrors.startTime ? "border-destructive" : ""}`}
                                        />
                                      </div>
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-[10px] uppercase text-muted-foreground">
                                        {t("fine")}
                                      </Label>
                                      <div className="flex gap-1">
                                        <DatePicker
                                          value={
                                            item.startDate
                                              ? item.endDate &&
                                                item.startDate <= item.endDate
                                                ? item.endDate
                                                : undefined
                                              : undefined
                                          }
                                          minDate={
                                            item.reservation?.arrival ||
                                            item.startDate ||
                                            new Date()
                                          }
                                          isDateDisabled={(date) =>
                                            isDateDisabled(
                                              date,
                                              item.serviceAvailability ?? 0,
                                              item.data ?? undefined,
                                              item.maxDailyQuantity,
                                              item.bookedDailyQuantities,
                                            )
                                          }
                                          onChange={(date) => {
                                            updateItemDates(
                                              item.id,
                                              item.startDate,
                                              date,
                                            );
                                            setStartDateTimeErrors((prev) => ({
                                              ...prev,
                                              [String(item.id)]: {
                                                ...(prev[String(item.id)] ?? {
                                                  startDate: false,
                                                  startTime: false,
                                                  endDate: false,
                                                  endTime: false,
                                                }),
                                                endDate: false,
                                              },
                                            }));
                                          }}
                                          placeholder={commonTranslations.t(
                                            "documentVerification.placeholders.datePlaceholder",
                                          )}
                                          className={`h-8 flex-1 text-xs ${itemStartErrors.endDate ? "border-destructive" : ""}`}
                                        />
                                        <TimePicker
                                          value={item.endTime ?? ""}
                                          filterTime={(time) =>
                                            isTimeInSlot(
                                              time,
                                              item.endDate ??
                                                item.startDate ??
                                                new Date(),
                                              item.serviceAvailability ?? 0,
                                              item.data ?? undefined,
                                            )
                                          }
                                          onChange={(time) => {
                                            updateItemTimes(
                                              item.id,
                                              item.startTime,
                                              time,
                                            );
                                            setStartDateTimeErrors((prev) => ({
                                              ...prev,
                                              [String(item.id)]: {
                                                ...(prev[String(item.id)] ?? {
                                                  startDate: false,
                                                  startTime: false,
                                                  endDate: false,
                                                  endTime: false,
                                                }),
                                                endTime: false,
                                              },
                                            }));
                                          }}
                                          className={`h-8 w-28 text-xs ${itemStartErrors.endTime ? "border-destructive" : ""}`}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          ) : (
            <ScrollArea className="h-full pr-1">
              <div className="space-y-4 py-2 p-3">
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="co-name">{t("name")}*</Label>
                    <Input
                      id="co-name"
                      value={checkoutForm.name}
                      onChange={(e) => {
                        setCheckoutForm((f) => ({
                          ...f,
                          name: e.target.value,
                        }));
                        setFormErrors((fe) => ({ ...fe, name: false }));
                      }}
                      className={formErrors.name ? "border-destructive" : ""}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="co-surname">{t("surname")}*</Label>
                    <Input
                      id="co-surname"
                      value={checkoutForm.surname}
                      onChange={(e) => {
                        setCheckoutForm((f) => ({
                          ...f,
                          surname: e.target.value,
                        }));
                        setFormErrors((fe) => ({ ...fe, surname: false }));
                      }}
                      className={formErrors.surname ? "border-destructive" : ""}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="co-email">{t("email")}*</Label>
                    <Input
                      id="co-email"
                      type="email"
                      value={checkoutForm.email}
                      onChange={(e) => {
                        setCheckoutForm((f) => ({
                          ...f,
                          email: e.target.value,
                        }));
                        setFormErrors((fe) => ({ ...fe, email: false }));
                      }}
                      className={formErrors.email ? "border-destructive" : ""}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="co-notes">{t("notes")}</Label>
                    <Textarea
                      id="co-notes"
                      value={checkoutForm.notes}
                      onChange={(e) =>
                        setCheckoutForm((f) => ({
                          ...f,
                          notes: e.target.value,
                        }))
                      }
                      className="resize-none"
                      rows={4}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="room">{t("room_number")}</Label>
                    <Input
                      id="room"
                      value={checkoutForm.roomNumber}
                      onChange={(e) =>
                        setCheckoutForm((f) => ({
                          ...f,
                          roomNumber: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
            </ScrollArea>
          )}
        </div>

        {items.length > 0 && (
          <SheetFooter className="flex-col gap-4 border-t pt-6 sm:flex-col">
            <div className="flex items-center justify-between w-full">
              <span className="text-base font-medium">{t("total")}</span>
              <span className="text-xl font-bold">
                €{totalPrice?.toFixed(2)}
              </span>
            </div>
            {showCheckout && (
              <div>
                <FieldGroup>
                  <Field orientation="horizontal">
                    <Checkbox
                      className={
                        formErrors.mandatoryConditions
                          ? "border-destructive"
                          : ""
                      }
                      id="terms-checkbox-1"
                      name="terms-checkbox-1"
                      onCheckedChange={(v: CheckedState) =>
                        setCheckoutForm((f) => ({
                          ...f,
                          mandatoryConditions: v === true,
                        }))
                      }
                    />
                    <FieldContent>
                      <div className="flex">
                        <FieldLabel htmlFor="terms-checkbox-1">
                          Accetto i termini e le condizioni
                        </FieldLabel>
                        <Button
                          type="button"
                          variant="ghost"
                          size={"sm"}
                          className="ml-2 inline-flex align-middle cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onOpenInfoPrivacyPolicy();
                          }}
                          aria-label={commonTranslations.t(
                            "verification.privacyPolicy",
                          )}
                        >
                          <Info className="h-4 w-4" />
                        </Button>
                      </div>
                      <FieldDescription className="text-xs">
                        Cliccando su questa checkbox, accetti i termini e le
                        condizioni d'utilizzo.
                      </FieldDescription>
                      {}
                      <FieldError>Questa condizione è obbligatoria</FieldError>
                    </FieldContent>
                  </Field>
                  <Field orientation="horizontal">
                    <Checkbox
                      id="terms-checkbox-2"
                      name="terms-checkbox-2"
                      onCheckedChange={(v: CheckedState) =>
                        setCheckoutForm((f) => ({
                          ...f,
                          marketing: v === true,
                        }))
                      }
                    />
                    <FieldContent>
                      <FieldLabel htmlFor="terms-checkbox-2">
                        Accetto di ricevere comunicazioni marketing
                      </FieldLabel>

                      <FieldDescription className="text-xs">
                        Acconsento l'uso della mia email per ricevere promozioni
                        o comunicazioni di marketing.
                      </FieldDescription>
                    </FieldContent>
                  </Field>
                </FieldGroup>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 w-full">
              {!showCheckout ? (
                <>
                  <Button
                    variant="outline"
                    className="cursor-pointer"
                    onClick={clearCart}
                  >
                    <Trash2 />
                    {t("empty")}
                  </Button>
                  <Button
                    className="cursor-pointer"
                    onClick={() => onCheckOut()}
                  >
                    <ShoppingCart />
                    {t("checkout")}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    className="col-span-2 cursor-pointer"
                    disabled={isLoading}
                    onClick={confirmCart}
                  >
                    {isLoading ? "..." : t("confirm")}
                  </Button>
                </>
              )}
            </div>
          </SheetFooter>
        )}
        <PrivacyPolicyDialog
          open={isPrivacyInfoDialogOpen}
          onOpenChange={setIsPrivacyInfoDialogOpen}
          title={commonTranslations.t("verification.privacyPolicy")}
          htmlContent={privacyPolicyHtml}
          fallbackText={commonTranslations.t(
            "verification.privacyPolicyDescription",
          )}
        />
      </SheetContent>
    </Sheet>
  );
}
